import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function BudgetForm() {
  const [budgetData, setBudgetData] = useState({
    personnel: '',
    equipment: '',
    operations: '',
    other: '',
    personnelNotes: '',
    equipmentNotes: '',
    operationsNotes: '',
    otherNotes: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const activeStep = 4;
  const steps = [
    { number: 1, title: 'Upload Document', description: 'Attach your application file' },
    { number: 2, title: 'Applicant Information', description: 'Enter contact and address details' },
    { number: 3, title: 'Project Information', description: 'Describe the project and funding needs' },
    { number: 4, title: 'Budget', description: 'Provide budget allocations' },
    { number: 5, title: 'DOL Determination', description: 'Complete final eligibility review' },
  ];

  useEffect(() => {
    const storedData = sessionStorage.getItem('newApplicationData');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.budget) {
          setBudgetData(parsed.budget);
        }
      } catch {
        sessionStorage.removeItem('newApplicationData');
      }
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setBudgetData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!budgetData.personnel.trim()) nextErrors.personnel = 'Personnel budget is required.';
    if (!budgetData.equipment.trim()) nextErrors.equipment = 'Equipment budget is required.';
    if (!budgetData.operations.trim()) nextErrors.operations = 'Operations budget is required.';
    if (!budgetData.other.trim()) nextErrors.other = 'Other budget is required.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    sessionStorage.setItem('newApplicationData', JSON.stringify({
      ...currentData,
      budget: { ...budgetData },
    }));
    navigate('/dashboard/new-application/dol');
  };

  return (
    <div className="page-stack">
        <section className="app-flow-header">
          <div>
            <p className="app-flow-tag">Application Flow</p>
            <h1>Budget Planning</h1>
            <p className="app-flow-description">
              Break down the proposal costs into key budget categories for clear review.
            </p>
          </div>
          <div className="step-badges">
            {steps.map((step) => (
              <div key={step.number} className={`step-item ${activeStep === step.number ? 'active' : ''}`}>
                <div className="step-number">{step.number}</div>
                <div className="step-text">
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="application-content">
          <div className="form-card">
            <h2 className="section-heading">Step 4: Budget</h2>
            <p className="form-description">
              Provide a detailed budget breakdown to support the funding request.
            </p>

            <form onSubmit={handleContinue} noValidate>
              <div className="budget-section">
                <h3>Personnel</h3>
                <label className="form-field">
                  <span>Budget Amount</span>
                  <input
                    type="text"
                    name="personnel"
                    value={budgetData.personnel}
                    onChange={handleChange}
                    className={errors.personnel ? 'input-error' : ''}
                    placeholder="e.g. $50,000"
                  />
                  {errors.personnel && <div className="field-error">{errors.personnel}</div>}
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    name="personnelNotes"
                    value={budgetData.personnelNotes}
                    onChange={handleChange}
                    placeholder="Describe personnel costs"
                    rows={3}
                  />
                </label>
              </div>

              <div className="budget-section">
                <h3>Equipment</h3>
                <label className="form-field">
                  <span>Budget Amount</span>
                  <input
                    type="text"
                    name="equipment"
                    value={budgetData.equipment}
                    onChange={handleChange}
                    className={errors.equipment ? 'input-error' : ''}
                    placeholder="e.g. $20,000"
                  />
                  {errors.equipment && <div className="field-error">{errors.equipment}</div>}
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    name="equipmentNotes"
                    value={budgetData.equipmentNotes}
                    onChange={handleChange}
                    placeholder="Describe equipment costs"
                    rows={3}
                  />
                </label>
              </div>

              <div className="budget-section">
                <h3>Operations</h3>
                <label className="form-field">
                  <span>Budget Amount</span>
                  <input
                    type="text"
                    name="operations"
                    value={budgetData.operations}
                    onChange={handleChange}
                    className={errors.operations ? 'input-error' : ''}
                    placeholder="e.g. $15,000"
                  />
                  {errors.operations && <div className="field-error">{errors.operations}</div>}
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    name="operationsNotes"
                    value={budgetData.operationsNotes}
                    onChange={handleChange}
                    placeholder="Describe operations costs"
                    rows={3}
                  />
                </label>
              </div>

              <div className="budget-section">
                <h3>Other</h3>
                <label className="form-field">
                  <span>Budget Amount</span>
                  <input
                    type="text"
                    name="other"
                    value={budgetData.other}
                    onChange={handleChange}
                    className={errors.other ? 'input-error' : ''}
                    placeholder="e.g. $5,000"
                  />
                  {errors.other && <div className="field-error">{errors.other}</div>}
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    name="otherNotes"
                    value={budgetData.otherNotes}
                    onChange={handleChange}
                    placeholder="Describe other budget requests"
                    rows={3}
                  />
                </label>
              </div>

              {submitError && <div className="section-error">{submitError}</div>}

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => navigate('/dashboard/new-application/project')}>
                  Back to Project Info
                </button>
                <button type="submit" className="submit-btn">
                  Save and Continue to DOL Determination
                </button>
              </div>
            </form>
          </div>
        </section>
    </div>
  );
}

export default BudgetForm;
